import { useState, useCallback } from "react";
import { ServiceRecord } from "../service-apis/serviceTypes";
import {
  ServiceApiService,
  PaginationParams,
  CreateServiceParams,
  UpdateServiceParams,
} from "../service-apis/serviceManagementService";

interface UseServiceManagementReturn {
  // 数据状态
  serviceList: ServiceRecord[];
  loading: boolean;
  total: number;

  // 操作状态
  submitting: boolean;

  // 操作函数
  fetchServiceList: (params?: PaginationParams) => Promise<void>;
  createService: (params: CreateServiceParams) => Promise<boolean>;
  updateService: (id: string, params: UpdateServiceParams) => Promise<boolean>;
  toggleServiceStatus: (record: ServiceRecord) => Promise<boolean>;
  deleteService: (record: ServiceRecord) => Promise<boolean>;
  healthCheck: (record: ServiceRecord) => Promise<boolean>;
  fetchServiceDetail: (id: string) => Promise<ServiceRecord | null>;

  // 内部方法（供上层 Hook 使用）
  setServiceList: (services: ServiceRecord[]) => void;
}

export const useServiceManagement = (): UseServiceManagementReturn => {
  const [serviceList, setServiceList] = useState<ServiceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [total, setTotal] = useState(0);

  // 获取微服务列表
  const fetchServiceList = useCallback(async (params: PaginationParams = {}) => {
    setLoading(true);
    try {
      const response = await ServiceApiService.getServiceList(params);
      const { services, total } = ServiceApiService.processServiceListResponse(response);
      // 调试信息已移除
      // 调试信息已移除
      setServiceList(services);
      setTotal(total);
    } catch (error) {
      console.error("[useServiceManagement] 获取服务列表失败:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 创建微服务
  const createService = useCallback(async (params: CreateServiceParams): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await ServiceApiService.createService(params);
      return response.success;
    } catch (error) {
      console.error("[useServiceManagement] 创建微服务失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 更新微服务
  const updateService = useCallback(async (id: string, params: UpdateServiceParams): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await ServiceApiService.updateService(id, params);
      return response.success;
    } catch (error) {
      console.error("[useServiceManagement] 更新微服务失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 切换微服务状态
  const toggleServiceStatus = useCallback(async (record: ServiceRecord): Promise<boolean> => {
    const newStatus = record.status === "online" ? "offline" : "online";
    setSubmitting(true);
    try {
      const response = await ServiceApiService.updateService(record.id, {
        name: record.name,
        backendUrl: record.backendUrl,
        url: record.url,
        description: record.description,
        healthCheckUrl: record.healthCheckUrl,
        status: newStatus,
      });
      return response.success;
    } catch (error) {
      console.error("[useServiceManagement] 切换服务状态失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 删除微服务
  const deleteService = useCallback(async (record: ServiceRecord): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await ServiceApiService.deleteService(record.id);
      return response.success;
    } catch (error) {
      console.error("[useServiceManagement] 删除微服务失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 健康检查
  const healthCheck = useCallback(async (record: ServiceRecord): Promise<boolean> => {
    setSubmitting(true);
    try {
      const response = await ServiceApiService.healthCheck(record.id);

      if (response.success) {
        // 更新服务列表中的健康状态
        setServiceList((prevList) =>
          prevList.map((service) =>
            service.id === record.id
              ? {
                ...service,
                healthStatus: response.data?.healthStatus,
                lastHealthCheck: response.data?.lastCheck || new Date().toISOString(),
              }
              : service
          )
        );
        return response.data?.healthStatus || false;
      }
      return false;
    } catch (error) {
      console.error("[useServiceManagement] 健康检查失败:", error);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  // 获取微服务详情
  const fetchServiceDetail = useCallback(async (id: string): Promise<ServiceRecord | null> => {
    setLoading(true);
    try {
      const response = await ServiceApiService.getServiceDetail(id);
      if (response.success && response.data) {
        return response.data as ServiceRecord;
      }
      return null;
    } catch (error) {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // 数据状态
    serviceList,
    loading,
    total,

    // 操作状态
    submitting,

    // 操作函数
    fetchServiceList,
    createService,
    updateService,
    toggleServiceStatus,
    deleteService,
    healthCheck,
    fetchServiceDetail,

    // 内部方法
    setServiceList,
  };
};

